export interface KoralmContentItem {
  id: string;
  title: string;
  summary?: string;
  category?: string | null;
  imageUrl?: string | null;
  previewImageUrl?: string | null;
  fullImageUrl?: string | null;
  accentColor?: string | null;
  publishedAt?: string | null;
  sourceUrl?: string | null;
  raw?: unknown;
}

export interface KoralmContentFetchOptions {
  limit?: number;
  categories?: string[];
  signal?: AbortSignal;
}

const FALLBACK_LIMIT = 1000;
const PAGE_SIZE = 200;
const DEFAULT_API_BASE = 'https://landesmuseum-api.arkturian.com';
const DEFAULT_STORAGE_BASE = 'https://api-storage.arkturian.com';

const globalCrypto: Crypto | undefined =
  typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto | undefined) : undefined;

interface StorageObjectRef {
  id?: number | string | null;
  mime_type?: string | null;
  mimeType?: string | null;
}

interface CandidateMedia {
  url?: string | null;
  thumbnail_url?: string | null;
  storage_object?: StorageObjectRef | null;
}

interface CandidateItem {
  id: number | string;
  title?: string | null;
  summary?: string | null;
  ai_summary_de?: string | null;
  ai_summary_en?: string | null;
  raw_text?: string | null;
  url?: string | null;
  published_at?: string | null;
  source_name?: string | null;
  category?: string | null;
  media?: CandidateMedia[] | null;
}

interface CandidatesResponse {
  items?: CandidateItem[];
}

export async function fetchKoralmContent(options: KoralmContentFetchOptions = {}): Promise<KoralmContentItem[]> {
  const apiBase = resolveApiBaseUrl();
  const storageBase = resolveStorageBaseUrl();
  const limit = options.limit ?? FALLBACK_LIMIT;
  const categories = options.categories && options.categories.length > 0
    ? options.categories
    : ['news', 'research', 'official', 'media', 'other'];

  const collected: KoralmContentItem[] = [];
  let offset = 0;

  while (!options.limit || collected.length < limit) {
    const remaining = options.limit ? Math.max(limit - collected.length, 0) : PAGE_SIZE;
    const pageLimit = options.limit ? Math.min(PAGE_SIZE, Math.max(remaining, 0)) : PAGE_SIZE;
    if (pageLimit === 0) break;

    const page = await fetchPage(apiBase, storageBase, pageLimit, offset, categories, options.signal);
    if (page.length === 0) {
      break;
    }

    for (const item of page) {
      collected.push(item);
      if (options.limit && collected.length >= limit) {
        break;
      }
    }

    offset += pageLimit;
  }

  console.info('[KoralmContent] Aggregated items', { total: collected.length });
  return collected.slice(0, options.limit ?? collected.length);
}

function resolveApiBaseUrl(): string {
  const explicit = (import.meta as any)?.env?.VITE_KORALMBAHN_API_URL;
  if (explicit && explicit.length > 0) {
    return explicit.replace(/\/+$/, '');
  }

  if (import.meta.env.DEV) {
    return 'http://localhost:8080';
  }

  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin.replace(/\/+$/, '');
  }

  return DEFAULT_API_BASE;
}

function resolveStorageBaseUrl(): string {
  const explicit = (import.meta as any)?.env?.VITE_KORALMBAHN_STORAGE_URL;
  if (explicit && explicit.length > 0) {
    return explicit.replace(/\/+$/, '');
  }
  return DEFAULT_STORAGE_BASE;
}

async function fetchPage(
  apiBase: string,
  storageBase: string,
  limit: number,
  offset: number,
  categories: string[],
  signal?: AbortSignal,
): Promise<KoralmContentItem[]> {
  const url = buildRequestUrl(apiBase, limit, offset, categories);
  console.info(`[KoralmContent] fetchPage limit=${limit} offset=${offset} url=${url}`);

  let response: Response;
  try {
    response = await fetch(url, { cache: 'no-store', signal });
  } catch (error) {
    console.error('[KoralmContent] request failed (network)', error);
    return [];
  }

  if (!response.ok) {
    console.warn(`[KoralmContent] request failed (${response.status}) at offset=${offset} limit=${limit}`);
    return [];
  }

  const payload = (await response.json().catch(() => null)) as CandidatesResponse | null;
  const items: CandidateItem[] = Array.isArray(payload?.items) ? payload!.items! : [];

  if (items.length === 0) {
    return [];
  }

  return items.map((item, index) => mapCandidateToKoralmItem(item, storageBase, offset + index));
}

function buildRequestUrl(apiBase: string, limit: number, offset: number, categories: string[]): string {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  params.append('status', 'imported');
  categories.forEach((category) => params.append('category', category));
  return `${apiBase}/admin/candidates?${params.toString()}`;
}

function mapCandidateToKoralmItem(item: CandidateItem, storageBase: string, fallbackOrder: number): KoralmContentItem {
  const id = createDeterministicId(item);
  const summary = extractSummary(item);
  const mediaUrls = resolveMediaUrls(item.media, storageBase);
  const category = item.category ?? null;
  const publishedAt = item.published_at ?? null;

  return {
    id,
    title: item.title ?? 'Unbetitelter Beitrag',
    summary,
    category,
    imageUrl: mediaUrls.preview ?? mediaUrls.full ?? null,
    previewImageUrl: mediaUrls.preview,
    fullImageUrl: mediaUrls.full,
    accentColor: null,
    publishedAt,
    sourceUrl: item.url ?? null,
    raw: item,
  };
}

function resolveMediaUrls(media: CandidateMedia[] | null | undefined, storageBase: string): { preview: string | null; full: string | null } {
  if (!media || media.length === 0) {
    return { preview: null, full: null };
  }

  for (const item of media) {
    const storageId = item?.storage_object?.id;
    const storageMime = (item?.storage_object?.mime_type ?? item?.storage_object?.mimeType ?? '').toLowerCase();

    if (storageId != null) {
      return {
        preview: buildSizedStorageUrl(storageBase, storageId, storageMime, 256),
        full: buildSizedStorageUrl(storageBase, storageId, storageMime, 1024),
      };
    }

    const candidate = item?.url ?? item?.thumbnail_url ?? null;
    if (candidate) {
      return {
        preview: candidate,
        full: candidate,
      };
    }
  }

  return { preview: null, full: null };
}

function buildSizedStorageUrl(storageBase: string, id: string | number, mime: string, width: number): string {
  const url = new URL(`${storageBase}/storage/media/${id}`);
  url.searchParams.set('width', String(width));
  url.searchParams.set('quality', '85');
  url.searchParams.set('format', mime.startsWith('application/pdf') ? 'jpg' : 'jpg');
  return url.toString();
}

function extractSummary(item: CandidateItem): string {
  const candidates = [
    item.ai_summary_de,
    item.summary,
    item.ai_summary_en,
    item.raw_text,
  ];
  for (const entry of candidates) {
    const normalized = normalizeSnippet(entry);
    if (normalized) {
      return normalized;
    }
  }
  return '';
}

function normalizeSnippet(value?: string | null): string {
  if (!value) return '';
  return stripHtml(value)
    .replace(/\s+/g, ' ')
    .trim();
}

function stripHtml(value: string): string {
  return value.replace(/<[^>]+>/g, ' ');
}

function createDeterministicId(item: CandidateItem): string {
  if (item.id !== undefined && item.id !== null) {
    return String(item.id);
  }
  if (item.url) {
    return item.url;
  }
  if (globalCrypto && typeof globalCrypto.randomUUID === 'function') {
    return globalCrypto.randomUUID();
  }
  return `item-${Math.random().toString(36).slice(2, 10)}`;
}


